/**
 * Currency helpers shared by the admin location editor, the payment popover and
 * the FX layer. Codes are lowercase ISO-4217 (e.g. 'gbp'); GBP is the app's
 * settlement currency. The symbol table and formatMoney live in
 * supabase/functions/_shared/currency.ts so emails format money identically.
 */

import { CURRENCY_SYMBOL, formatMoney } from "@shared/currency";

export { CURRENCY_SYMBOL, formatMoney };

/** Currencies offered in admin dropdowns (location default + payment entry). */
export const CURRENCIES = [
  "gbp",
  "eur",
  "usd",
  "ils",
  "rub",
  "pln",
  "czk",
  "gel",
  "amd",
] as const;

/** Symbol for a code, or the upper-cased code itself when unknown. */
export function currencySymbol(code: string | null | undefined): string {
  const c = (code ?? "gbp").toLowerCase();
  return CURRENCY_SYMBOL[c] ?? c.toUpperCase();
}

/** Symbol for the PWYW "5–20+" range. The range is GBP-anchored, so only show it
 *  in currencies of comparable magnitude (USD/EUR/AUD/GBP). Everything smaller
 *  (e.g. AMD ~500/£) would misprice the range, so fall back to £. */
const PWYW_COMPARABLE = new Set(["gbp", "eur", "usd", "aud"]);
export function pwywSymbol(code: string | null | undefined): string {
  const c = (code ?? "gbp").toLowerCase();
  if (!PWYW_COMPARABLE.has(c)) return "£";
  return c === "aud" ? "A$" : currencySymbol(c);
}

/**
 * Public per-location ticket price line. A real price_cents renders that amount
 * (specials, or any priced event). No price set means Quizrun's tiered model:
 * pay what you want — NOT free-for-all (the first-regular-event comp is a
 * per-user truth resolved in the register flow, never promised on the plate).
 * Virtual is no exception — it follows the same rule as any onsite event.
 */
export function formatPrice(
  cents: number | null | undefined,
  currency?: string | null,
): string {
  if (cents != null && cents > 0) return formatMoney(cents, currency ?? null);
  return "Pay what you want";
}

/**
 * The "amount + readonly ≈GBP" pair shown by the Paid badge (event + participant
 * pages). `originalCents`/`originalCurrency` is what the payer handed over;
 * `gbpCents` is the normalised settlement amount. Returns nulls when there's no
 * recorded amount, and the ≈GBP equivalent only when the charge wasn't GBP.
 */
export function formatPaidAmount(
  originalCents: number | null | undefined,
  originalCurrency: string | null | undefined,
  gbpCents: number | null | undefined,
): { amountText: string | null; gbpEquiv: string | null } {
  if (originalCents == null || originalCents <= 0) {
    // Several payments in different currencies have no single original leg, but
    // the normalised GBP total is still meaningful — show that rather than
    // nothing.
    if (gbpCents != null && gbpCents > 0) {
      return { amountText: formatMoney(gbpCents, "gbp"), gbpEquiv: null };
    }
    return { amountText: null, gbpEquiv: null };
  }
  const currency = (originalCurrency ?? "gbp").toLowerCase();
  return {
    amountText: formatMoney(originalCents, currency),
    gbpEquiv:
      currency !== "gbp" && gbpCents != null
        ? formatMoney(gbpCents, "gbp")
        : null,
  };
}

/** One row of v_member_payment.payments — a single payment against the event. */
export interface PaymentBreakdownEntry {
  id: number;
  amount_cents: number | null;
  amount_original: number | null;
  currency_original: string | null;
  exchange_rate: number | null;
  method: string | null;
  /** False for anything Stripe recorded: shown, never edited or removed. */
  is_manual: boolean;
}

/**
 * One line per payment for the Paid tooltip, e.g. ["£15 · card", "£5 · cash"].
 * A person may legitimately pay more than once for an event (a top-up, a tip,
 * or part cash / part card), and the badge shows only the total — so the
 * breakdown is what makes that total explicable. Every payment is listed, so
 * the lines always sum to the total; PaidBadge decides when to show them.
 */
export function formatPaymentBreakdown(
  payments: PaymentBreakdownEntry[] | null | undefined,
): string[] {
  if (!payments) return [];
  return payments.map((p) => {
    const money =
      p.amount_original != null && p.amount_original > 0
        ? formatMoney(p.amount_original, p.currency_original)
        : formatMoney(p.amount_cents ?? 0, "gbp");
    return p.method ? `${money} · ${p.method}` : money;
  });
}
